import { PortalClientError } from '@kraaapf/portal-client';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { api, setUnauthenticatedHandler } from '@/app/api';

import type { CurrentUser } from '@kraaapf/contracts';

/**
 * Wer angemeldet ist.
 *
 * ## Warum das in Pinia liegt und nicht in TanStack Query
 *
 * Weil es **Anwendungszustand** ist und keine Serverdaten (Entscheidung L7).
 * Der Unterschied ist nicht akademisch: Serverdaten haben eine Gültigkeit, ein
 * Neuladen und einen Zwischenspeicher; „bin ich angemeldet" hat das nicht — es
 * gilt oder es gilt nicht, und wenn es nicht mehr gilt, zeigt die Anwendung
 * etwas völlig anderes an.
 *
 * ## Was hier NICHT liegt
 *
 * Kein Token. Die Sitzung steht in zwei `httpOnly`-Cookies, die kein Skript
 * lesen kann (Entscheidung B5). Dieser Store hält nur, was der Master über
 * `GET /auth/me` erzählt — und das ist nichts, was nicht ohnehin auf dem
 * Bildschirm stünde.
 */
export const useAuthStore = defineStore('auth', () => {
  const user = ref<CurrentUser | null>(null);

  /**
   * `null` heißt „noch nicht geprüft", nicht „nicht angemeldet".
   *
   * Der Unterschied entscheidet, was die Anwendung beim Start zeigt: Ohne ihn
   * blitzte der Anmeldebildschirm für einen Moment auf, bevor `me()` antwortet
   * — bei jedem Neuladen, für jeden angemeldeten Betreiber.
   */
  const checked = ref(false);
  const busy = ref(false);

  const isAuthenticated = computed(() => user.value !== null);

  /**
   * Fragt den Master, wer angemeldet ist.
   *
   * Ein 401 ist hier **kein Fehler**, sondern die Antwort „niemand". Alles
   * andere wird weitergereicht: Ein nicht erreichbarer Master soll nicht als
   * Abmeldung erscheinen.
   */
  async function load(): Promise<void> {
    busy.value = true;

    try {
      user.value = await api.auth.me();
    } catch (error) {
      if (error instanceof PortalClientError && error.status === 401) {
        user.value = null;
      } else {
        throw error;
      }
    } finally {
      checked.value = true;
      busy.value = false;
    }
  }

  /**
   * Meldet an.
   *
   * Gibt `'totp-required'` zurück, wenn der zweite Faktor fehlt — das ist kein
   * Fehler, sondern ein Zustand: Der erste Faktor stimmte.
   */
  async function login(input: {
    email: string;
    password: string;
    totpCode?: string;
    rememberMe: boolean;
  }): Promise<'authenticated' | 'totp-required'> {
    busy.value = true;

    try {
      const result = await api.auth.login({
        email: input.email,
        password: input.password,
        rememberMe: input.rememberMe,
        ...(input.totpCode === undefined ? {} : { totpCode: input.totpCode }),
      });

      if (result.status === 'authenticated') {
        user.value = result.user;
        checked.value = true;
      }

      return result.status;
    } finally {
      busy.value = false;
    }
  }

  async function logout(): Promise<void> {
    try {
      await api.auth.logout();
    } finally {
      // Auch wenn der Aufruf scheitert: Wer abmelden wollte, ist hier draußen.
      // Die Sitzung im Master läuft dann eben mit ihrer Frist aus.
      user.value = null;
      checked.value = true;
    }
  }

  /** Der Client meldet, dass die Sitzung endgültig abgelaufen ist. */
  function forget(): void {
    user.value = null;
    checked.value = true;
  }

  setUnauthenticatedHandler(forget);

  return { user, checked, busy, isAuthenticated, load, login, logout, forget };
});
